import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Box,
  Icon,
  ScrollArea,
  Switch,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import type { ReactNode } from "react";

export function SettingsSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    // The title is a 32px capsule, so the section's inset carries the rest of the 16px rhythm.
    <Box direction="column" overflowX="clip" paddingY="md">
      <Accordion defaultValue={[title]}>
        <AccordionItem separator={false} value={title}>
          <Box paddingX="lg">
            <AccordionTrigger chrome="wash">{title}</AccordionTrigger>
          </Box>
          <AccordionPanel ink="inherit" padding="none">
            <Box direction="column" gap="md" paddingBottom="md" paddingTop="md" paddingX="lg">
              {children}
            </Box>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
}

/** Half the pane height, so the last section scrolls to mid-pane. */
export function PaneScrollGutter() {
  // `cqh` of the PaneScroller's container; a percentage in its content box cannot reach it.
  return <Box aria-hidden height="50cqh" shrink={false} />;
}

export function PaneScroller({ children }: { children: ReactNode }) {
  return (
    <ScrollArea className="[container-type:size]" scrollFade>
      {children}
    </ScrollArea>
  );
}

export function SwitchRow({
  label,
  description,
  info,
  checked,
  disabled = false,
  onCheckedChange,
}: {
  label: string;
  description?: string | undefined;
  info?: string | undefined;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <Box align="center" direction="row" gap="md" justify="between">
      <Box direction="column" gap="2xs" minWidth={0}>
        <Box align="center" direction="row" gap="xs">
          <Text size="sm">{label}</Text>
          {info ? (
            <Tooltip>
              <TooltipTrigger aria-label={`About ${label}`} render={<Box align="center" />}>
                <Icon color="muted" name="info" size="xs" />
              </TooltipTrigger>
              <TooltipPopup className="max-w-60">{info}</TooltipPopup>
            </Tooltip>
          ) : null}
        </Box>
        {description ? (
          <Text size="xs" variant="muted">
            {description}
          </Text>
        ) : null}
      </Box>
      <Switch
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </Box>
  );
}
